import { useCallback, useEffect, useRef, useState } from "react";
import { getComposerStatus } from "../../../services/apiService";

/**
 * Is the AI line composer available on this server?
 *
 * The composer needs an API key and a grounding document that only exist in
 * some deployments, so the entry point has to be able to not exist at all.
 * Two rules follow from that:
 *
 *   FAIL CLOSED. Any failure — network, 401, 503, a malformed body — resolves
 *   to `available: false`. A "Draft with AI" button that errors on click is
 *   worse than no button: it promises a capability the server doesn't have.
 *
 *   ASK ONCE. The status is a per-deployment fact, not per-component state, so
 *   the in-flight promise is cached at module scope. N mounts (rail footer,
 *   dialog, editor) make one request per page load, not N.
 */

let statusPromise = null;

/** Test seam: drop the cached probe so each test starts from a clean slate. */
export function resetComposerAvailability() {
  statusPromise = null;
}

function loadOnce(loadStatus) {
  if (!statusPromise) {
    statusPromise = Promise.resolve()
      .then(() => loadStatus())
      .then((payload) => ({
        available: payload?.available === true,
        model: payload?.model || null,
      }))
      .catch(() => ({ available: false, model: null }));
  }
  return statusPromise;
}

export function useComposerAvailability({
  loadStatus = getComposerStatus,
} = {}) {
  const [state, setState] = useState({
    available: false,
    model: null,
    checking: true,
  });
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    loadOnce(loadStatus).then((result) => {
      if (!mounted.current) return;
      setState({ ...result, checking: false });
    });
    return () => {
      mounted.current = false;
    };
  }, [loadStatus]);

  /**
   * A 503 from /draft means the server lost the composer after the probe said
   * yes (key rotated out, doc removed). Hide it for the rest of the session
   * rather than letting the estimator keep hitting a dead button.
   */
  const markUnavailable = useCallback(() => {
    statusPromise = Promise.resolve({ available: false, model: null });
    if (mounted.current) {
      setState({ available: false, model: null, checking: false });
    }
  }, []);

  return { ...state, markUnavailable };
}

export default useComposerAvailability;
